<template>
  <div class="min-h-screen bg-gray-50">
    <div class="bg-white shadow-sm border-b border-gray-200">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-gray-900">Услуги</h1>
            <p class="mt-1 text-sm text-gray-500">Управление услугами</p>
          </div>
          <button
            @click="openCreateModal"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <i class="pi pi-plus mr-2"></i>
            Добавить услугу
          </button>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div v-if="loading" class="flex justify-center items-center py-12">
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"
        ></div>
      </div>

      <div
        v-else-if="error"
        class="bg-white rounded-lg shadow-sm border border-red-200 p-6"
      >
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <i class="pi pi-exclamation-triangle text-red-400 text-xl"></i>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Ошибка загрузки</h3>
            <div class="mt-2 text-sm text-red-700">{{ error }}</div>
          </div>
        </div>
        <div class="mt-4">
          <button
            @click="fetchEntities"
            class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <i class="pi pi-refresh mr-2"></i>
            Повторить
          </button>
        </div>
      </div>

      <div v-else-if="!entities.length" class="text-center py-12">
        <div
          class="mx-auto h-24 w-24 flex items-center justify-center rounded-full bg-gray-100"
        >
          <i class="pi pi-cog text-gray-400 text-3xl"></i>
        </div>
        <h3 class="mt-4 text-lg font-medium text-gray-900">
          Услуги не найдены
        </h3>
        <p class="mt-2 text-sm text-gray-500">
          Нажмите "Добавить услугу", чтобы создать первую услугу в системе
        </p>
        <div class="mt-6">
          <button
            @click="openCreateModal"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <i class="pi pi-plus mr-2"></i>
            Создать услугу
          </button>
        </div>
      </div>

      <div v-else class="bg-white shadow-sm rounded-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  ID
                </th>
                <th
                  class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Название
                </th>
                <th
                  class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Консультация
                </th>
                <th
                  class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Действия
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="entity in entities"
                :key="entity.id"
                :class="[
                  entity.is_deleted
                    ? 'bg-gray-100 text-gray-400'
                    : 'hover:bg-gray-50',
                  'transition-colors duration-150'
                ]"
              >
                <td class="px-6 py-4 whitespace-nowrap">
                  <span
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    #{{ entity.id }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">
                    {{ entity.name }}
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-center">
                  <i
                    v-if="entity.is_consulting"
                    class="pi pi-check text-green-600 text-lg"
                    title="Консультация"
                  ></i>
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                >
                  <div class="flex items-center justify-end space-x-2">
                    <button
                      v-if="!entity.is_deleted"
                      @click="openEditModal(entity)"
                      class="text-blue-600 hover:text-blue-900 transition-colors duration-150"
                      title="Редактировать"
                    >
                      <i class="pi pi-pencil"></i>
                    </button>
                    <button
                      v-if="!entity.is_deleted"
                      @click="openDeleteModal(entity)"
                      class="text-red-600 hover:text-red-900 transition-colors duration-150"
                      title="Удалить"
                    >
                      <i class="pi pi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="bg-gray-50 px-6 py-3 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <span class="text-sm text-gray-500">
              Всего записей: {{ totalCount }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          @click="closeModal"
        ></div>

        <div
          class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
        >
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                  {{
                    modalMode === 'create'
                      ? 'Создать услугу'
                      : 'Редактировать услугу'
                  }}
                </h3>

                <div class="space-y-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Название услуги *
                    </label>
                    <input
                      v-model="formData.name"
                      type="text"
                      placeholder="Введите название услуги"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      :class="{
                        'border-red-300 focus:ring-red-500 focus:border-red-500':
                          !formData.name && formSubmitted
                      }"
                    />
                    <p
                      v-if="!formData.name && formSubmitted"
                      class="mt-1 text-sm text-red-600"
                    >
                      Название обязательно
                    </p>
                  </div>

                  <div>
                    <label class="flex items-center">
                      <input
                        v-model="formData.is_consulting"
                        type="checkbox"
                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span class="ml-2 text-sm text-gray-700">
                        Консультационная услуга
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              @click="submitForm"
              :disabled="submitting"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <span
                v-if="submitting"
                class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"
              ></span>
              {{ modalMode === 'create' ? 'Создать' : 'Сохранить' }}
            </button>
            <button
              @click="closeModal"
              :disabled="submitting"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Отмена
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Модальное окно подтверждения удаления -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          @click="closeDeleteModal"
        ></div>

        <div
          class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
        >
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div
                class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10"
              >
                <i class="pi pi-exclamation-triangle text-red-600 text-xl"></i>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Подтверждение удаления
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500">
                    Вы действительно хотите удалить услугу
                    <strong>"{{ entityToDelete?.name }}"</strong>?
                  </p>
                  <p class="text-sm text-gray-500 mt-2">
                    Это действие нельзя отменить.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              @click="confirmDelete"
              :disabled="submitting"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              <span
                v-if="submitting"
                class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"
              ></span>
              Удалить
            </button>
            <button
              @click="closeDeleteModal"
              :disabled="submitting"
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Отмена
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, reactive, onMounted, watch } from 'vue'
  import { useToast } from 'primevue/usetoast'
  import { useEntity } from '../composables/useEntity.js'

  export default {
    name: 'ServiceList',
    setup() {
      const toast = useToast()

      const showModal = ref(false)
      const showDeleteModal = ref(false)
      const formSubmitted = ref(false)
      const formValid = ref(false)
      const entityToDelete = ref(null)

      const formData = reactive({
        name: '',
        is_consulting: false
      })

      const {
        entities,
        totalCount,
        loading,
        error,
        submitting,
        modalMode,
        selectedEntity,
        fetchEntities,
        createEntity,
        updateEntity,
        deleteEntity,
        openCreateModal,
        openEditModal,
        handleSubmit
      } = useEntity('service')

      watch(modalMode, newMode => {
        if (newMode === 'create') {
          Object.assign(formData, {
            name: '',
            is_consulting: false
          })
        } else if (newMode === 'edit' && selectedEntity.value) {
          Object.assign(formData, {
            name: selectedEntity.value.name || '',
            is_consulting: selectedEntity.value.is_consulting || false
          })
        }
      })

      watch(selectedEntity, newEntity => {
        if (modalMode.value === 'edit' && newEntity) {
          Object.assign(formData, {
            name: newEntity.name || '',
            is_consulting: newEntity.is_consulting || false
          })
        }
      })

      const openCreateModalHandler = () => {
        openCreateModal()
        showModal.value = true
        formSubmitted.value = false
      }

      const openEditModalHandler = entity => {
        openEditModal(entity)
        showModal.value = true
        formSubmitted.value = false
      }

      const openDeleteModal = entity => {
        entityToDelete.value = entity
        showDeleteModal.value = true
      }

      const closeModal = () => {
        showModal.value = false
        formSubmitted.value = false
      }

      const closeDeleteModal = () => {
        showDeleteModal.value = false
        entityToDelete.value = null
      }

      const confirmDelete = async () => {
        if (!entityToDelete.value) return

        try {
          await deleteEntity(entityToDelete.value.id)
          closeDeleteModal()
          await fetchEntities()

          toast.add({
            severity: 'success',
            summary: 'Успешно',
            detail: 'Услуга удалена',
            life: 3000
          })
        } catch (_error) {
          toast.add({
            severity: 'error',
            summary: 'Ошибка',
            detail: 'Произошла ошибка при удалении услуги',
            life: 3000
          })
        }
      }

      const submitForm = async () => {
        formSubmitted.value = true

        if (!formData.name) {
          toast.add({
            severity: 'error',
            summary: 'Ошибка',
            detail: 'Пожалуйста, заполните обязательные поля',
            life: 3000
          })
          return
        }
        const dataToSend = { ...formData }

        try {
          const response = await handleSubmit(dataToSend)

          if (!response) {
            throw new Error('Ошибка при сохранении')
          }

          closeModal()
          await fetchEntities()

          toast.add({
            severity: 'success',
            summary: 'Успешно',
            detail:
              modalMode.value === 'create'
                ? 'Услуга создана'
                : 'Услуга обновлена',
            life: 3000
          })
        } catch (err) {
          const errorData = err.response?.data
          let errorMessage = 'Произошла ошибка при сохранении'
          
      const details = errorData?.data?.details || errorData?.details
      
      if (details) {
        const firstField = Object.keys(details)[0]
        if (firstField) {
          const fieldError = details[firstField]
          if (Array.isArray(fieldError) && fieldError.length > 0) {
            errorMessage = `${firstField}: ${fieldError[0]}`
          } else if (typeof fieldError === 'string') {
            errorMessage = `${firstField}: ${fieldError}`
          }
        }
      } else if (errorData?.data?.message) {
            errorMessage = errorData.data.message
          } else if (errorData?.message) {
            errorMessage = errorData.message
          } else if (err.message) {
            errorMessage = err.message
          }
          
          toast.add({
            severity: 'error',
            summary: 'Ошибка',
            detail: errorMessage,
            life: 5000
          })
        }
      }

      onMounted(() => {
        fetchEntities()
      })

      return {
        showModal,
        showDeleteModal,
        formSubmitted,
        formValid,
        formData,
        entities,
        totalCount,
        loading,
        error,
        submitting,
        modalMode,
        selectedEntity,
        fetchEntities,
        createEntity,
        updateEntity,
        deleteEntity,
        openCreateModal: openCreateModalHandler,
        openEditModal: openEditModalHandler,
        openDeleteModal,
        closeModal,
        closeDeleteModal,
        confirmDelete,
        submitForm
      }
    }
  }
</script>

<style scoped>
  .service-list {
    min-height: 100vh;
  }

  :deep(.p-datatable) {
    border-radius: 0;
  }

  .card-header {
    padding: var(--spacing-6);
  }
</style>
